import { formatInventoryNumber } from "./inventoryPurchasing";

function cleanPdfText(value) {
  return String(value ?? "")
    .replace(/€/g, "EUR")
    .split("")
    .map((char) => {
      const code = char.charCodeAt(0);
      if (code === 9 || code === 10 || code === 13 || (code >= 32 && code <= 255)) {
        return char;
      }
      return "?";
    })
    .join("");
}

function escapePdfText(value) {
  return cleanPdfText(value)
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");
}

function wrapText(text, maxChars = 95) {
  const words = cleanPdfText(text).split(/\s+/).filter(Boolean);
  const lines = [];
  let current = "";

  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > maxChars && current) {
      lines.push(current);
      current = word;
      return;
    }
    current = next;
  });

  if (current) lines.push(current);
  return lines.length ? lines : [""];
}

function wrapQuantityText(text) {
  const cleanText = cleanPdfText(text);
  const match = cleanText.match(/^(.*?)(\s*\(.+\))$/);

  if (match) {
    return [
      ...wrapText(match[1].trim(), 18),
      ...wrapText(match[2].trim(), 18),
    ].slice(0, 3);
  }

  return wrapText(cleanText, 18).slice(0, 3);
}

function pdfNumber(value) {
  return Number(value).toFixed(2).replace(/\.?0+$/, "");
}

function estimateTextWidth(value, size = 10) {
  return cleanPdfText(value).length * size * 0.52;
}

function pdfDate(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleDateString("de-DE");
  } catch {
    return String(value);
  }
}

export function getOrderSupplierLabel(order, noSupplierLabel) {
  return (order.supplier_name || order.supplier || "").trim() || noSupplierLabel;
}

export function groupOrdersBySupplier(orders, noSupplierLabel) {
  const groups = new Map();

  orders.forEach((order) => {
    const supplierLabel = getOrderSupplierLabel(order, noSupplierLabel);
    const supplierId = order.supplier_id ? String(order.supplier_id) : "";
    const key = supplierId ? `id:${supplierId}` : `name:${supplierLabel}`;

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        supplierLabel,
        items: [],
        totalCost: 0,
      });
    }

    const group = groups.get(key);
    group.items.push(order);
    group.totalCost += Number(order.estimated_cost) || 0;
  });

  return Array.from(groups.values()).sort((a, b) =>
    a.supplierLabel.localeCompare(b.supplierLabel),
  );
}

export function formatOrderListQuantity(order) {
  if (order.purchase_units_ordered && order.package_label) {
    return `${formatInventoryNumber(order.purchase_units_ordered)} x ${order.package_label} (${formatInventoryNumber(order.suggested_quantity)} ${order.unit || ""})`;
  }
  return `${formatInventoryNumber(order.suggested_quantity)} ${order.unit || ""}`.trim();
}

export function buildOrderListPdfBytes({ supplierLabel, orders, labels, generatedAt = new Date() }) {
  const pageWidth = 595;
  const pageHeight = 842;
  const margin = 42;
  const tableWidth = pageWidth - margin * 2;
  const pages = [];
  let current;
  let y;

  const color = {
    ink: [15, 23, 42],
    muted: [100, 116, 139],
    line: [226, 232, 240],
    soft: [248, 250, 252],
    band: [30, 64, 175],
    bandDark: [15, 23, 42],
    accent: [14, 165, 233],
  };

  const rgb = ([r, g, b]) => `${pdfNumber(r / 255)} ${pdfNumber(g / 255)} ${pdfNumber(b / 255)}`;
  const add = (command) => current.commands.push(command);
  const rect = (x, bottom, width, height, fillColor, strokeColor = null) => {
    add("q");
    if (fillColor) add(`${rgb(fillColor)} rg`);
    if (strokeColor) add(`${rgb(strokeColor)} RG 0.8 w`);
    add(`${pdfNumber(x)} ${pdfNumber(bottom)} ${pdfNumber(width)} ${pdfNumber(height)} re ${fillColor && strokeColor ? "B" : fillColor ? "f" : "S"}`);
    add("Q");
  };
  const text = (value, x, baseline, { size = 10, font = "F1", fill = color.ink } = {}) => {
    add(`BT ${rgb(fill)} rg /${font} ${pdfNumber(size)} Tf ${pdfNumber(x)} ${pdfNumber(baseline)} Td (${escapePdfText(value)}) Tj ET`);
  };
  const textRight = (value, rightX, baseline, options = {}) => {
    const size = options.size || 10;
    text(value, rightX - estimateTextWidth(value, size), baseline, options);
  };
  const rule = (x1, baseline, x2, strokeColor = color.line) => {
    add(`q ${rgb(strokeColor)} RG 0.7 w ${pdfNumber(x1)} ${pdfNumber(baseline)} m ${pdfNumber(x2)} ${pdfNumber(baseline)} l S Q`);
  };
  const startPage = () => {
    current = { commands: [], hasTableHeader: false };
    pages.push(current);
    y = 792;
    rect(0, 752, pageWidth, 90, color.bandDark);
    rect(0, 752, 9, 90, color.accent);
    text(labels.title, margin, 803, { size: 24, font: "F2", fill: [255, 255, 255] });
    text(labels.subtitle || "", margin, 781, { size: 10.5, fill: [191, 219, 254] });
    textRight(`${labels.generatedAt}: ${pdfDate(generatedAt)}`, pageWidth - margin, 803, { size: 10, fill: [219, 234, 254] });
    y = 712;
  };
  const ensureSpace = (height) => {
    if (!current || y - height < 86) startPage();
  };
  const drawInfoPanels = () => {
    const panelTop = y;
    const panelHeight = 76;
    const leftWidth = 292;
    const rightX = margin + leftWidth + 22;
    const rightWidth = tableWidth - leftWidth - 22;

    rect(margin, panelTop - panelHeight, leftWidth, panelHeight, [255, 255, 255], color.line);
    text(labels.supplier, margin + 14, panelTop - 19, { size: 8.5, font: "F2", fill: color.muted });
    wrapText(supplierLabel, 34).slice(0, 2).forEach((line, index) => {
      text(line, margin + 14, panelTop - 42 - index * 13, { size: 12, font: "F2" });
    });

    rect(rightX, panelTop - panelHeight, rightWidth, panelHeight, [248, 250, 252], color.line);
    text(labels.orderDetails || "Order details", rightX + 14, panelTop - 19, { size: 8.5, font: "F2", fill: color.muted });
    text(labels.generatedAt, rightX + 14, panelTop - 40, { size: 9, fill: color.muted });
    textRight(pdfDate(generatedAt), rightX + rightWidth - 14, panelTop - 40, { size: 9.5, font: "F2" });
    text(labels.items || "Items", rightX + 14, panelTop - 57, { size: 9, fill: color.muted });
    textRight(String(orders.length), rightX + rightWidth - 14, panelTop - 57, { size: 9.5, font: "F2" });

    y -= panelHeight + 34;
  };
  const drawTableHeader = () => {
    ensureSpace(32);
    rect(margin, y - 28, tableWidth, 28, color.band);
    text(labels.product, margin + 34, y - 18, { size: 8.5, font: "F2", fill: [255, 255, 255] });
    text(labels.articleNumber, margin + 236, y - 18, { size: 8.5, font: "F2", fill: [255, 255, 255] });
    text(labels.quantity, margin + 310, y - 18, { size: 8.5, font: "F2", fill: [255, 255, 255] });
    y -= 28;
    current.hasTableHeader = true;
  };

  startPage();
  drawInfoPanels();
  drawTableHeader();

  orders.forEach((order, index) => {
    const productLines = wrapText(order.inventory_item_name || labels.unknown, 29).slice(0, 3);
    const quantityLines = wrapQuantityText(formatOrderListQuantity(order));
    const detailLineCount = Math.max(productLines.length, quantityLines.length);
    const rowHeight = 36 + detailLineCount * 12;

    ensureSpace(rowHeight + 10);
    if (!current.hasTableHeader) drawTableHeader();
    const rowTop = y;
    const rowBottom = y - rowHeight;
    rect(margin, rowBottom, tableWidth, rowHeight, index % 2 === 0 ? [255, 255, 255] : color.soft, color.line);
    rule(margin, rowBottom, margin + tableWidth);

    text(String(index + 1).padStart(2, "0"), margin + 10, rowTop - 20, { size: 8.5, font: "F2", fill: color.accent });
    productLines.forEach((line, lineIndex) => {
      text(line, margin + 34, rowTop - 20 - lineIndex * 12, { size: lineIndex === 0 ? 10 : 9, font: lineIndex === 0 ? "F2" : "F1" });
    });
    text(order.article_number || "-", margin + 236, rowTop - 20, { size: 9.2 });
    quantityLines.forEach((line, lineIndex) => {
      text(line, margin + 310, rowTop - 20 - lineIndex * 12, { size: 9.2 });
    });
    y = rowBottom;
  });

  pages.forEach((page, pageIndex) => {
    current = page;
    rule(margin, 54, pageWidth - margin, color.line);
    text(`${pageIndex + 1} / ${pages.length}`, pageWidth - margin - 30, 34, { size: 9, fill: color.muted });
  });

  const objects = [];
  const pageObjectIds = [];
  const fontObjectIds = { F1: 3, F2: 4, F3: 5 };

  objects[1] = "<< /Type /Catalog /Pages 2 0 R >>";
  objects[3] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>";
  objects[4] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>";
  objects[5] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Oblique /Encoding /WinAnsiEncoding >>";

  pages.forEach((page, pageIndex) => {
    const pageObjectId = 6 + pageIndex * 2;
    const contentObjectId = pageObjectId + 1;
    pageObjectIds.push(pageObjectId);
    const stream = page.commands.join("\n");
    objects[pageObjectId] = `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 ${fontObjectIds.F1} 0 R /F2 ${fontObjectIds.F2} 0 R /F3 ${fontObjectIds.F3} 0 R >> >> /Contents ${contentObjectId} 0 R >>`;
    objects[contentObjectId] = `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`;
  });

  objects[2] = `<< /Type /Pages /Kids [${pageObjectIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pageObjectIds.length} >>`;

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  for (let id = 1; id < objects.length; id += 1) {
    if (!objects[id]) continue;
    offsets[id] = pdf.length;
    pdf += `${id} 0 obj\n${objects[id]}\nendobj\n`;
  }

  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length}\n`;
  pdf += "0000000000 65535 f \n";
  for (let id = 1; id < objects.length; id += 1) {
    pdf += `${String(offsets[id] || 0).padStart(10, "0")} 00000 n \n`;
  }
  pdf += `trailer\n<< /Size ${objects.length} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

  return Uint8Array.from(pdf, (char) => char.charCodeAt(0));
}

export function buildOrderListPdfFilename(supplierLabel, date = new Date()) {
  const safeSupplier = cleanPdfText(supplierLabel)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "supplier";
  const datePart = date.toISOString().slice(0, 10);
  return `purchase-order-${safeSupplier}-${datePart}.pdf`;
}
